import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, X, Trash2, Play } from 'lucide-react';
import { isVideoExtension } from '@/utils/mediaUtils';
import MediaComments from './MediaComments';

interface SlideShowModalProps {
  isOpen: boolean;
  images: string[];
  initialIndex?: number;
  onClose: () => void;
  onDelete?: (index: number) => void;
  isDeleting?: boolean;
  vehicleId?: string;
}

const SlideShowModal: React.FC<SlideShowModalProps> = ({
  isOpen,
  images,
  initialIndex = 0,
  onClose,
  onDelete,
  isDeleting = false,
  vehicleId,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const mediaContainerRef = useRef<HTMLDivElement>(null);
  const mediaRef = useRef<HTMLImageElement | HTMLVideoElement>(null);

  useEffect(() => {
    setCurrentIndex(initialIndex);
    resetZoom();
    setIsVideoPlaying(false);
  }, [initialIndex, isOpen]);

  // Block body scroll while modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const resetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handlePrevious = () => {
    resetZoom();
    setIsVideoPlaying(false);
    (mediaRef.current as HTMLVideoElement | null)?.pause?.();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    resetZoom();
    setIsVideoPlaying(false);
    (mediaRef.current as HTMLVideoElement | null)?.pause?.();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleVideoToggle = () => {
    const video = mediaRef.current as HTMLVideoElement | null;
    if (!video) return;
    if (isVideoPlaying) {
      video.pause();
      setIsVideoPlaying(false);
    } else {
      video.play();
      setIsVideoPlaying(true);
    }
  };

  // Handle touch swipe gestures
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      setTouchStart(e.targetTouches[0].clientX);
      setPanStart({ x: e.targetTouches[0].clientX, y: e.targetTouches[0].clientY });
    } else if (e.touches.length === 2) {
      setTouchStart(0);
      setTouchEnd(0);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (e.changedTouches.length === 1 && !isPanning) {
      setTouchEnd(e.changedTouches[0].clientX);
      handleSwipe();
    }
  };

  const handleSwipe = () => {
    if (zoom > 1) return;
    if (touchStart - touchEnd > 50) handleNext();
    else if (touchEnd - touchStart > 50) handlePrevious();
  };

  // Handle pinch zoom
  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
      if (!mediaRef.current?.dataset.initialDistance) {
        mediaRef.current?.dataset && (mediaRef.current.dataset.initialDistance = String(distance));
      } else {
        const initialDistance = parseFloat(mediaRef.current?.dataset.initialDistance || '0');
        setZoom(Math.max(1, Math.min(3, (distance / initialDistance) * (zoom || 1))));
      }
      const containerRect = mediaContainerRef.current?.getBoundingClientRect();
      if (containerRect) {
        setPan({
          x: (touch1.clientX + touch2.clientX) / 2 - containerRect.width / 2,
          y: (touch1.clientY + touch2.clientY) / 2 - containerRect.height / 2,
        });
      }
    } else if (e.touches.length === 1 && zoom > 1) {
      setIsPanning(true);
      const touch = e.touches[0];
      setPan({
        x: Math.max(Math.min(touch.clientX - panStart.x, (zoom - 1) * 100), -(zoom - 1) * 100),
        y: Math.max(Math.min(touch.clientY - panStart.y, (zoom - 1) * 100), -(zoom - 1) * 100),
      });
    }
  };

  const handleTouchEndZoom = (e: React.TouchEvent) => {
    if (e.touches.length < 2) {
      mediaRef.current?.dataset && delete mediaRef.current.dataset.initialDistance;
    }
    setIsPanning(false);
  };

  // Handle wheel zoom
  const handleWheel = (e: React.WheelEvent) => {
    if (!mediaContainerRef.current) return;
    e.preventDefault();
    setZoom(Math.max(1, Math.min(3, zoom + (e.deltaY > 0 ? -0.1 : 0.1))));
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') handlePrevious();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  // Guard against undefined or invalid index
  if (!images || images.length === 0) return null;

  // Ensure currentIndex is within bounds
  const validIndex = Math.min(currentIndex, Math.max(0, images.length - 1));
  const currentMedia = images[validIndex];

  if (!currentMedia) return null;

  const isVideo = isVideoExtension(currentMedia);

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)' }}>
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:opacity-75 transition-opacity z-20"
        aria-label="Close slideshow"
      >
        <X size={28} />
      </button>

      {/* Counter */}
      <div className="absolute top-6 left-6 text-white text-lg font-semibold z-20">
        {validIndex + 1} / {images.length}
      </div>

      {/* Media Container */}
      <div
        ref={mediaContainerRef}
        className="relative w-full flex items-center justify-center px-4 select-none overflow-hidden flex-shrink-0"
        style={{ height: vehicleId ? '48vh' : '70vh' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={(e) => { handleTouchEnd(e); handleTouchEndZoom(e); }}
        onWheel={handleWheel}
      >
        {isVideo ? (
          <div className="relative w-full h-full flex items-center justify-center">
            <video
              key={currentMedia}
              ref={mediaRef as React.RefObject<HTMLVideoElement>}
              src={currentMedia}
              className="select-none object-contain"
              style={{
                transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
                transformOrigin: 'center',
                transition: isPanning ? 'none' : 'transform 0.2s ease-out',
                maxWidth: '100%', maxHeight: '100%', width: '100%', height: '100%',
              }}
              playsInline
              crossOrigin="anonymous"
              onEnded={() => setIsVideoPlaying(false)}
            />
            <button
              onClick={handleVideoToggle}
              className="absolute inset-0 flex items-center justify-center"
              style={{ background: 'transparent' }}
            >
              {!isVideoPlaying && (
                <div className="bg-black bg-opacity-50 rounded-full p-4">
                  <Play size={36} className="text-white" fill="white" />
                </div>
              )}
            </button>
          </div>
        ) : (
          <img
            ref={mediaRef as React.RefObject<HTMLImageElement>}
            src={currentMedia}
            alt={`Slide ${validIndex + 1}`}
            className="pointer-events-none select-none object-contain"
            draggable={false}
            style={{
              transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
              transformOrigin: 'center',
              transition: isPanning ? 'none' : 'transform 0.2s ease-out',
              maxWidth: '100%', maxHeight: '100%', width: '100%', height: '100%',
            }}
          />
        )}

        {/* Previous Button */}
        <button
          onClick={handlePrevious}
          className="absolute left-4 bg-background hover:bg-gray-100 text-black rounded-full p-3 transition-all"
          aria-label="Previous image"
        >
          <ChevronLeft size={32} />
        </button>

        {/* Next Button */}
        <button
          onClick={handleNext}
          className="absolute right-4 bg-background hover:bg-gray-100 text-black rounded-full p-3 transition-all"
          aria-label="Next image"
        >
          <ChevronRight size={32} />
        </button>
      </div>

      {/* Comments — keyed by mediaUrl so state resets on navigation */}
      {vehicleId && (
        <MediaComments key={currentMedia} vehicleId={vehicleId} mediaUrl={currentMedia} />
      )}

      {/* Bottom Controls */}
      <div className="flex-shrink-0 flex flex-col items-center gap-3 pb-5 pt-2">
        <p className="text-gray-400 text-sm">Swipe to navigate</p>
        {onDelete && (
          <button
            onClick={() => onDelete(currentIndex)}
            disabled={isDeleting}
            className="w-11/12 max-w-sm text-white font-semibold py-4 px-4 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-3"
            style={{ backgroundColor: '#fc2837' }}
          >
            {isDeleting ? (
              <>
                <span>Deleting...</span>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              </>
            ) : (
              <>
                <span>Delete</span>
                <Trash2 size={20} />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default SlideShowModal;
